import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Clock3, RefreshCw } from 'lucide-react';
import { apiErrorMessage, getActivityTimeline } from '../api/abfApi';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { INK } from '../onboarding/review/reviewStyles';
import { Layout, Note, SmallButton, SummaryGrid, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import type { ActivityTimeline, TimelineEvent } from '../types/board';
import { Table, when, words } from '../board/boardShared';
import { facilityWorkspacePath } from '../routes';
import type { AbfWorkspaceKey } from '../types/facilityWorkspace';
import { WorkspaceGuide, WorkspaceTools } from '../components/WorkspaceExperience';
import { humanizeToken } from '../presentation/format';

/**
 * Slice 8 / FR-8.4 — the activity timeline is a server read model: one sequence over contract,
 * collateral, borrowing base, funding, covenants, collections, reporting, exceptions and
 * administration, filtered by workflow, actor and period, paged. The browser never merges
 * read models.
 *
 * On the review standard since the walkthrough of 2026-09-22 (with F-S8-002): the summary strip
 * says how much record there is and where the page sits in it; the rail is the workflow filter
 * with the actor and period filters in its footer; the work pane is the timeline grouped by
 * day. A log has no decision, so there is no decision bar.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; margin-top: 14px;
  h2, h3, p { margin: 0; } h2 { font-size: 17px; } h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;
const Strip = styled(SummaryGrid)`
  grid-template-columns: repeat(5, minmax(0, 1fr));
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const Day = styled.h3`
  margin: 18px 0 6px; padding-bottom: 6px; border-bottom: 1px solid ${INK.line};
  color: ${INK.strong}; font-size: 13px; font-weight: 650; letter-spacing: .02em;
  &:first-of-type { margin-top: 0; }
  small { color: ${INK.muted}; font-weight: 400; margin-left: 8px; }
`;
const Pager = styled.div`
  display: flex; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 14px; color: ${INK.muted}; font-size: 13px;
`;
const Filter = styled.label`
  display: block; margin-top: 10px; font-size: 12px; color: ${INK.muted};
  input { width: 100%; box-sizing: border-box; margin-top: 4px; padding: 8px; background: ${INK.well}; color: ${INK.body}; border: 1px solid ${INK.lineStrong}; border-radius: 6px; font: inherit; font-size: 13px; }
`;

const tone = (kind: string): StatusTone => {
  const k = kind.toUpperCase();
  if (/(ACTIVE|APPROVED|RELEASED|FINAL|BOOKED|SIGNED_OFF|CERTIFIED|DELIVERED|ACKNOWLEDGED|RESOLVED|CONFIRMED|POSTED|PASS|SUCCESS|RECEIVED|SENT)$/.test(k)) return 'done';
  if (/(BREACH|REJECTED|QUARANTINED|FAILED|WITHDRAWN|REVERSED|CANCELLED|EXPIRED)$/.test(k)) return 'critical';
  if (/(DRAFT|QUEUED|PENDING|PROPOSED|OPEN|VERIFIED|SCHEDULED|EXTRACTED|REVIEWED|INVESTIGATING)/.test(k)) return 'progress';
  if (/(RESTATED|DISCARDED|SUPERSEDED|DISMISSED)/.test(k)) return 'neutral';
  return 'warning';
};
const dayOf = (at: string) => new Date(at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const timeOf = (at: string) => new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const PAGE = 50;

/**
 * Event labels carry covenant codes as the engine names them (NET_LOSS_RATE); Monitoring shows
 * "Net loss rate". Only ALL_CAPS_UNDERSCORE tokens are touched, so file names and source refs
 * such as abf:mtm:7 pass through unchanged.
 */
const inWords = (text: string | null | undefined) =>
  String(text ?? '')
    // A one-word covenant code has no underscore to find it by: "Covenant OVERCOLLATERALIZATION pass".
    .replace(/\bCovenant ([A-Z][A-Z0-9]{3,})\b/g, (_m, code: string) => `Covenant ${humanizeToken(code).toLowerCase()}`)
    .replace(/[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+/g, (token, offset: number, whole: string) => {
    // A token glued to a path, a file name or a reference (ABF-RCR-2026-2_2026-08_v1.json, COLLECTIONS_BREAK:5:8) stays as it is.
    const before = whole[offset - 1];
    const after = whole[offset + token.length];
    if ((before && /[\w.:/-]/.test(before)) || (after && /[\w.:/-]/.test(after))) return token;
    return humanizeToken(token);
  });

/** Where a record lives, for the tables whose workspace is unambiguous. Anything else stays text. */
const RECORD_ROUTES: Record<string, { workspace: AbfWorkspaceKey; query?: (e: TimelineEvent) => string | null }> = {
  abf_covenant_run: { workspace: 'monitoring' },
  abf_operational_exception: { workspace: 'monitoring' },
  abf_collection_period: { workspace: 'collections', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_waterfall_run: { workspace: 'collections', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_lender_cash_booking: { workspace: 'collections', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_draw_request: { workspace: 'funding', query: e => `request=${e.record_id}` },
  abf_borrowing_base_run: { workspace: 'collateral', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_servicer_tape_ingest: { workspace: 'collateral', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_report_instance: { workspace: 'reporting', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_report_delivery: { workspace: 'reporting', query: e => e.period_id ? `period=${e.period_id}` : null },
  abf_outbound_exchange: { workspace: 'reporting' },
  abf_facility_lifecycle_event: { workspace: 'funding' },
};
const recordHref = (facilityId: number, e: TimelineEvent): string | null => {
  const route = RECORD_ROUTES[e.record_table];
  if (!route) return null;
  const query = route.query?.(e);
  return facilityWorkspacePath(facilityId, route.workspace) + (query ? `?${query}` : '');
};

interface Props { facilityId: number; onFeedback: (feedback: ActionFeedbackState) => void }

const ActivityWorkspace: React.FC<Props> = ({ facilityId, onFeedback }) => {
  const [timeline, setTimeline] = useState<ActivityTimeline | null>(null);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [workflow, setWorkflow] = useState('');
  const [actor, setActor] = useState('');
  const [params] = useSearchParams();
  const periodId = params.get('period') || params.get('periodId') || '';
  const [allPeriods, setAllPeriods] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTimeline(await getActivityTimeline(facilityId, { workflow: workflow || undefined, actor: actor || undefined, periodId: !allPeriods && periodId ? Number(periodId) : undefined, limit: PAGE, offset }));
    } catch (failure) {
      const message = apiErrorMessage(failure, 'The activity timeline could not be loaded.');
      setFeedback({ tone: 'error', message }); onFeedback({ tone: 'error', message });
    } finally { setLoading(false); }
  }, [facilityId, workflow, actor, periodId, allPeriods, offset, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  const days = useMemo(() => {
    const groups: Array<{ day: string; events: TimelineEvent[] }> = [];
    (timeline?.events || []).filter(e => `${e.label} ${e.detail || ''} ${e.actor || ''} ${e.record_id}`.toLowerCase().includes(search.trim().toLowerCase())).forEach(event => {
      const day = dayOf(event.at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.events.push(event); else groups.push({ day, events: [event] });
    });
    return groups;
  }, [timeline, search]);

  if (!timeline) return <Shell><Note>Loading the activity timeline…</Note></Shell>;
  const periodLabel = (id: number | string) => timeline.periods?.find(p => Number(p.id) === Number(id))?.period_label || `#${id}`;
  const total = timeline.total;
  const newest = timeline.events[0];
  const periodsOnPage = Array.from(new Set(timeline.events.map(e => e.period_id).filter(Boolean))) as number[];
  const filtered = Boolean(workflow || actor || periodId);

  return <Shell>
    <WorkspaceGuide><h2>Follow the facility’s business record</h2><p>Filter by workflow, actor or period, then open the source record for its evidence. Fund reports and holder statements are included through their facility references. This timeline shows recorded workflow states; it is not a complete history of every state transition.</p></WorkspaceGuide>
    <Strip aria-label="Activity summary">
      <div><span>Events{filtered ? ' matching' : ''}</span><strong>{total}</strong></div>
      <div><span>Showing</span><strong>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE, total)}`}</strong></div>
      <div><span>Workflows</span><strong>{timeline.workflows.length}</strong></div>
      <div><span>Actors</span><strong>{timeline.actors.length}</strong></div>
      <div><span>Newest{filtered ? ' matching' : ''}</span><strong>{newest ? when(newest.at) : '—'}</strong></div>
    </Strip>

    <Layout>
      <TaskRail aria-label="Activity filters">
        <div className="rail-heading"><h2>Workflows</h2><p>Every workflow's own record, in one sequence. Pick one to narrow the timeline.</p></div>
        <div className="tasks">
          <TaskButton type="button" $active={workflow === ''} onClick={() => { setWorkflow(''); setOffset(0); }}>
            <strong>All workflows</strong>
          </TaskButton>
          {timeline.workflows.map(w => <TaskButton key={w} type="button" $active={workflow === w} onClick={() => { setWorkflow(w); setOffset(0); }}>
            <strong>{words(w)}</strong>
          </TaskButton>)}
        </div>
        <div className="rail-footer">
          <label style={{ marginTop: 0 }}>Actor
            <select value={actor} onChange={e => { setActor(e.target.value); setOffset(0); }}>
              <option value="">anyone</option>
              {timeline.actors.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <Filter>Review period {periodId ? periodLabel(periodId) : 'not selected'}
            <span style={{display:'block', marginTop:8}}><input type="checkbox" style={{width:'auto'}} checked={allPeriods} onChange={e => {setAllPeriods(e.target.checked); setOffset(0);}} /> Search all periods</span>
          </Filter>
          {periodsOnPage.length > 0 && <p>On this page: {periodsOnPage.map(periodLabel).join(', ')}</p>}
          {filtered && <p><button type="button" style={{ background: 'none', border: 0, color: INK.accentSoft, cursor: 'pointer', padding: 0, font: 'inherit', fontSize: 12 }} onClick={() => { setWorkflow(''); setActor(''); setAllPeriods(false); setOffset(0); }}>Clear filters</button></p>}
        </div>
      </TaskRail>

      <Work aria-label="Activity timeline">
        <div className="work-heading">
          <div><h2>Activity</h2><p>{workflow ? `${words(workflow)} · ` : ''}{actor ? `${actor} · ` : ''}{allPeriods ? 'all periods · ' : periodId ? `period ${periodLabel(periodId)} · ` : ''}newest first, {PAGE} per page.</p></div>
          <SmallButton type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}</SmallButton>
        </div>

        <WorkspaceTools><input type="search" aria-label="Search activity on this page" placeholder="Find an event, actor or record on this page…" value={search} onChange={e => setSearch(e.target.value)} /><span>Search applies to this page of {PAGE} records</span></WorkspaceTools>
        {days.length === 0 && <Note>No event matches the filters.</Note>}
        {days.map(group => <React.Fragment key={group.day}>
          <Day>{group.day}<small>{group.events.length} event{group.events.length === 1 ? '' : 's'}</small></Day>
          <Table>
            <thead><tr><th style={{ width: '6rem' }}>Time</th><th style={{ width: '9rem' }}>Workflow</th><th>Event</th><th style={{ width: '9rem' }}>Actor</th><th style={{ width: '13rem' }}>Record</th></tr></thead>
            <tbody>
              {group.events.map((e, i) => <tr key={`${e.record_table}-${e.record_id}-${i}`}>
                <td className="muted" style={{ whiteSpace: 'nowrap' }}><Clock3 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{timeOf(e.at)}</td>
                <td>{e.workflow === 'AI_ASSISTED' ? <StatusChip tone="progress" label="AI-assisted" /> : words(e.workflow)}</td>
                <td><StatusChip tone={tone(e.kind)} label={words(e.kind.replace(/^[A-Z_]+?_/, ''))} /> {inWords(e.label)}{e.detail ? <><br /><small style={{ color: INK.muted }}>{inWords(e.detail)}</small></> : null}</td>
                <td>{e.actor || <span style={{ color: INK.muted }}>system</span>}</td>
                <td className="muted">{e.workflow === 'AI_ASSISTED' ? 'Confirmed copilot note' : (() => {
                  const href = recordHref(facilityId, e);
                  const raw = `${e.record_table}:${e.record_id}`;
                  const label = `${words(e.record_table.replace(/^abf_/, ''))} #${e.record_id}`;
                  return <>
                    {href ? <Link to={href} title={raw} style={{ color: INK.accentSoft }}>{label}</Link> : <code title={raw}>{label}</code>}
                    {e.period_id ? ` · ${periodLabel(e.period_id)}` : ''}
                  </>;
                })()}</td>
              </tr>)}
            </tbody>
          </Table>
        </React.Fragment>)}

        <Pager>
          <SmallButton type="button" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Newer</SmallButton>
          <span>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE, total)}`} of {total}</span>
          <SmallButton type="button" disabled={offset + PAGE >= total || loading} onClick={() => setOffset(offset + PAGE)}>Older</SmallButton>
        </Pager>
        <ActionLocalFeedback feedback={feedback} />
      </Work>
    </Layout>
  </Shell>;
};

export default ActivityWorkspace;
